import { Check, FileText, Info, ShieldCheck, X } from "lucide-react";
import { Reveal } from "./motion";

const STEPS = [
  { n: "01", t: "Upload a PDF", d: "Drag in a bank statement — nothing else to set up." },
  { n: "02", t: "Parsed in your browser", d: "The file is read on your device; it isn't sent anywhere." },
  { n: "03", t: "AI sorts each line", d: "Every transaction is categorised for you automatically." },
  { n: "04", t: "You confirm what's saved", d: "Tick the rows you want. Only those are kept." },
];

const RECEIPT = [
  { icon: FileText, t: "The original PDF file", s: "Stays on your device", ok: true },
  { icon: Check, t: "The rows you approve", s: "Saved to your account", ok: true },
  { icon: X, t: "The rows you don't tick", s: "Never saved", ok: false },
];

export function PrivacySection() {
  return (
    <section id="privacy" className="bg-background">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 py-24 lg:grid-cols-2 lg:px-10">
        <div>
          <Reveal>
            <span className="text-sm font-bold uppercase tracking-widest text-primary-text">Private by design</span>
            <h2 className="mt-4 text-4xl font-extrabold leading-tight tracking-tight text-foreground">
              Your statement is read on your device.
            </h2>
            <p className="mt-5 max-w-lg text-lg leading-relaxed text-muted-foreground">
              Import a PDF and it's parsed right inside your browser. AI sorts every line, you tick
              what's real, and only those rows are saved — nothing is uploaded to be read for you.
            </p>
          </Reveal>

          <div className="mt-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.06}>
                <div className={`flex items-start gap-4 py-4 ${i < STEPS.length - 1 ? "border-b border-border" : ""}`}>
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 font-mono text-sm font-bold text-primary-text">
                    {s.n}
                  </span>
                  <div>
                    <div className="text-base font-bold text-foreground">{s.t}</div>
                    <div className="mt-0.5 text-sm leading-relaxed text-muted-foreground">{s.d}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.1} y={32}>
          <div className="flex flex-col items-center gap-4 lg:pt-6">
            <div className="w-full max-w-md rounded-3xl border border-border bg-card p-7 shadow-xl">
              <div className="flex items-center justify-between">
                <span className="text-base font-bold text-foreground">What happens to your statement</span>
                <ShieldCheck className="h-5 w-5 text-[#3BA776]" />
              </div>
              <div className="my-4 h-px bg-border" />
              <ul className="space-y-4">
                {RECEIPT.map((r) => (
                  <li key={r.t} className="flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-xl bg-muted/60">
                      <r.icon className={`h-4 w-4 ${r.ok ? "text-[#3BA776]" : "text-muted-foreground/60"}`} />
                    </span>
                    <div>
                      <div className={`text-sm font-semibold ${r.ok ? "text-foreground" : "text-muted-foreground"}`}>{r.t}</div>
                      <div className="text-xs font-medium text-muted-foreground">{r.s}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <p className="flex max-w-md items-start gap-2 px-1 text-xs leading-relaxed text-muted-foreground/80">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Want to connect a bank directly? That's coming, and it'll be optional — importing a
              statement never needs it.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
